/**
 * @file useProtectedImage.js
 * @brief Fetches protected media with the current bearer token and exposes local blob URLs.
 */
import { useState, useEffect, useMemo } from "react";
import { useUnifiedAuth as useAuth } from "./useUnifiedAuth";

function normalizeProtectedUrl(value) {
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const lowered = trimmed.toLowerCase();
    if (lowered === "null" || lowered === "undefined") return null;
    return trimmed;
}

/**
 * Fetches a single protected URL with the bearer token,
 * returns a local blob: URL (or null while loading / on error).
 */
export function useProtectedImage(url) {
    const auth = useAuth();
    const [blobUrl, setBlobUrl] = useState(null);
    const normalizedUrl = useMemo(() => normalizeProtectedUrl(url), [url]);

    useEffect(() => {
        if (!normalizedUrl) {
            setBlobUrl(null);
            return;
        }

        let objectUrl = null;
        let cancelled = false;

        const load = async () => {
            try {
                const headers = {};
                if (auth.user?.access_token) {
                    headers["Authorization"] = `Bearer ${auth.user.access_token}`;
                }
                const res = await fetch(normalizedUrl, { headers });
                if (!res.ok || cancelled) return;
                const blob = await res.blob();
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setBlobUrl(objectUrl);
            } catch {
                // silently ignore — caller shows a placeholder
            }
        };

        load();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [normalizedUrl, auth.user?.access_token]);

    return blobUrl;
}

/**
 * Fetches multiple protected URLs with the bearer token,
 * returns an array of blob: URLs in the same order (nulls for failed ones).
 */
export function useProtectedImages(urls) {
    const auth = useAuth();
    const [blobUrls, setBlobUrls] = useState([]);
    const safeUrls = useMemo(
        () => (Array.isArray(urls) ? urls.map((value) => normalizeProtectedUrl(value)) : []),
        [urls]
    );
    const urlsKey = useMemo(() => JSON.stringify(safeUrls), [safeUrls]);

    useEffect(() => {
        if (!safeUrls.length) {
            setBlobUrls([]);
            return;
        }

        let objectUrls = [];
        let cancelled  = false;

        const load = async () => {
            try {
                const headers = {};
                if (auth.user?.access_token) {
                    headers["Authorization"] = `Bearer ${auth.user.access_token}`;
                }
                const results = await Promise.all(
                    safeUrls.map(async (url) => {
                        if (!url) return null;
                        const res = await fetch(url, { headers });
                        if (!res.ok) return null;
                        const blob = await res.blob();
                        return URL.createObjectURL(blob);
                    })
                );
                if (cancelled) {
                    results.forEach((u) => u && URL.revokeObjectURL(u));
                    return;
                }
                objectUrls = results.filter(Boolean);
                setBlobUrls(results);
            } catch {
                // silently ignore
            }
        };

        load();

        return () => {
            cancelled = true;
            objectUrls.forEach((u) => u && URL.revokeObjectURL(u));
        };
    }, [auth.user?.access_token, urlsKey]);

    return blobUrls;
}